import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { repositoryCall } from "./repositoryResult.js";
import { requireCmtsGuid } from "../utils/cmtsGuid.js";

function optionalGuid(value: unknown, label: string): string | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  return requireCmtsGuid(value.trim(), label);
}

function requiredText(value: unknown, fallback: string): string {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

function optionalDateIso(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) throw new Error("Escalation due date is invalid.");
  return parsed.toISOString();
}

function toEscalationRequest(
  payload: Record<string, unknown>,
  tenantId?: string,
): Record<string, unknown> {
  const ownerId = optionalGuid(
    payload.ownerId ?? payload.recipientUserId ?? payload.targetUserId,
    "Escalation ownerId",
  );

  return {
    tenantId,
    complaintId: requireCmtsGuid(
      payload.complaintId ?? payload.linkedEntityId,
      "Escalation complaintId",
    ),
   escalationType: requiredText(
  payload.escalationType ??
    payload.triggerType ??
    payload.triggerTypeCode,
  "MANUAL",
).toUpperCase(),
    triggerTypeId: optionalGuid(payload.triggerTypeId, "Escalation triggerTypeId"),
    escalationLevel: requiredText(payload.escalationLevel ?? payload.levelNumber, "1"),
    ownerId,
    recipientUserId: ownerId,
    recipientRoleId: optionalGuid(payload.recipientRoleId, "Escalation recipientRoleId"),
    reason: requiredText(
      payload.reason ?? payload.summary ?? payload.description,
      "Manual escalation",
    ),
    remarks:
      typeof payload.remarks === "string" && payload.remarks.trim()
        ? payload.remarks.trim()
        : undefined,
    dueDate: optionalDateIso(payload.dueDate),
  };
}

export function createEscalationRepository(client: CmtsHttpClient) {
  return {
    queue: (tenantId?: string) =>
      repositoryCall(
        () => client.get<unknown>(CMTS_API.escalation.queue, { query: { tenantId } }),
        "CMTS_ESCALATION_ALERT_QUEUE",
      ),
    create: (payload: Record<string, unknown>, tenantId?: string) =>
      repositoryCall(
        () => client.post<Record<string, unknown>>(
          CMTS_API.escalation.create,
          toEscalationRequest(payload, tenantId),
          { query: { tenantId } },
        ),
        "CMTS_ESCALATION_CREATE",
      ),
    update: (id: string, payload: Record<string, unknown>, tenantId?: string) =>
      repositoryCall(
        () => client.put<Record<string, unknown>>(
          `${CMTS_API.escalation.create}/${encodeURIComponent(requireCmtsGuid(id, "Escalation id"))}`,
          toEscalationRequest(payload, tenantId),
          { query: { tenantId } },
        ),
        "CMTS_ESCALATION_UPDATE",
      ),
    acknowledge: (id: string, tenantId?: string) =>
      repositoryCall(
        () => client.post<Record<string, unknown>>(
          CMTS_API.escalation.acknowledge(requireCmtsGuid(id, "Escalation id")),
          {},
          { query: { tenantId } },
        ),
        "CMTS_ESCALATION_ACKNOWLEDGE",
      ),
    resolve: (id: string, tenantId?: string) =>
      repositoryCall(
        () => client.post<Record<string, unknown>>(
          CMTS_API.escalation.resolve(requireCmtsGuid(id, "Escalation id")),
          {},
          { query: { tenantId } },
        ),
        "CMTS_ESCALATION_RESOLVE",
      ),
    rules: (tenantId?: string) =>
      repositoryCall(
        () => client.get<unknown>(CMTS_API.escalation.rules, { query: { tenantId } }),
        "CMTS_ESCALATION_RULES_MASTER",
      ),
    evaluate: (tenantId?: string) =>
      repositoryCall(
        () => client.post<unknown>(CMTS_API.escalation.evaluate, {}, { query: { tenantId } }),
        "CMTS_ESCALATION_EVALUATE",
      ),
  };
}